import { LoadingState } from "@repo/app-types/client/client";
import type { AdminApiAiUsage } from "@repo/app-types/entities/creditUsage";
import type { AdminApiEntitlementUsage } from "@repo/app-types/entities/entitlement";
import { getTeammateLicenseTypeForProductKey } from "@repo/app-types/entities/teammate";
import { BillingPlansEnumToDisplayName } from "@repo/app-types/helpers/billingFeatureGating";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Link, linkStyle } from "@repo/ui/Link/index";
import { capitalize, Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { IconChartBar, IconPencil } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import {
  useAiUsageQuery,
  useAudioUsageQuery,
  useCompanyEntitlementsQuery,
  useLicenseUtilizationQuery,
} from "../../../api/entities/company.js";
import { appPaths } from "../../../app/appPaths.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  fetchBillingInfo,
  selectDefaultPaymentMethod,
} from "../../billing/billingSlice.js";
import { selectCompany } from "../../company/companySlice.js";
import { teammateLicenseCopy } from "../teammateSettings/teammateLicenses.js";
import {
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

/**
 * The company's recurring credit allowance: the grant, plus whatever is left
 * on purchased packs. A contract granting no recurring credits has no
 * allowance to name, so it reports what was consumed instead.
 */
function formatMonthlyCredits(usage: AdminApiAiUsage): string {
  const { balances } = usage;
  if (usage.included_ai_credits === null) {
    return `${formatWholeNumber(usage.credits_used)} used`;
  }
  const included = `${formatWholeNumber(usage.included_ai_credits)} included`;
  return balances.kind === "live" && balances.remaining_purchased_credits > 0
    ? `${included} + ${formatWholeNumber(balances.remaining_purchased_credits)} purchased remaining`
    : included;
}

export const PlanSection: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const [portalLoadingState, setPortalLoadingState] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const defaultPaymentMethod = useAppSelector(selectDefaultPaymentMethod);
  const {
    data: entitlements,
    isLoading: isLoadingEntitlements,
    isError: isErrorEntitlements,
  } = useCompanyEntitlementsQuery();
  const { data: licenseUtilization } = useLicenseUtilizationQuery();
  // The current period's usage; deduped with AiCreditsSection's initial query.
  const { data: currentUsage } = useAiUsageQuery({ periodOffset: 0 });
  const { data: currentAudioUsage } = useAudioUsageQuery({ periodOffset: 0 });

  useEffect(() => {
    dispatch(fetchBillingInfo());
  }, [dispatch]);

  const openStripeBillingPortal = async (isPaymentMethodUpdate?: boolean) => {
    setPortalLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const path = `/billing/portal-session${
        isPaymentMethodUpdate ? "?type=payment_method_update" : ""
      }`;
      const { data } = await axios.post(path);
      setPortalLoadingState(LoadingState.LOADED);

      // Navigate to the portal url rather than opening in popup
      // so we don't have listen to webhooks when payment methods are added
      window.location.href = data;
    } catch {
      setPortalLoadingState(LoadingState.ERROR);
    }
  };

  const entitlementColumns: readonly EntityTableColumnDefinition<AdminApiEntitlementUsage>[] =
    useMemo(
      () => [
        {
          id: "starts_at",
          isRowHeader: true,
          header: "Start date",
          className: "w-[25%]",
          renderCell: (row) =>
            DateTime.fromISO(row.starts_at).toFormat("MMM dd yyyy"),
        },
        {
          id: "expires_at",
          header: "Expiration date",
          className: "w-[25%]",
          renderCell: (row) =>
            DateTime.fromISO(row.expires_at).toFormat("MMM dd yyyy"),
        },
        {
          id: "max_interviews",
          header: "Max interviews",
          className: "w-[25%]",
          renderCell: (row) => row.max_interviews.toString(),
        },
        {
          id: "used_interviews",
          header: "Used interviews",
          className: "w-[25%]",
          renderCell: (row) => row.used_interviews.toString(),
        },
      ],
      [],
    );

  if (!company) {
    return null;
  }

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Your plan</h2>
        <div className="overflow-hidden rounded-md border border-border-light bg-white">
          <div className="flex flex-col justify-between gap-4 px-4 py-3 sm:flex-row sm:items-center">
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
                <IconChartBar className="size-4.5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  {company.billing_current_plan
                    ? `${BillingPlansEnumToDisplayName[company.billing_current_plan]} plan`
                    : "Alpharun plan"}
                </p>
                {defaultPaymentMethod && defaultPaymentMethod.is_card ? (
                  <div className="group mt-0.5 flex items-center gap-1 text-sm text-muted-foreground">
                    <span>
                      {`${capitalize(defaultPaymentMethod.brand)} ending in ${
                        defaultPaymentMethod.last4
                      } · Expires ${
                        defaultPaymentMethod.exp_month >= 10 ? "" : "0"
                      }${defaultPaymentMethod.exp_month}/${
                        defaultPaymentMethod.exp_year
                      }`}
                    </span>
                    <div className="hidden group-hover:flex">
                      <IconButton
                        variant={ButtonVariant.Tertiary}
                        icon={<IconPencil className="h-3 w-3" />}
                        onClick={() => {
                          const isPaymentMethodUpdate = true;
                          openStripeBillingPortal(isPaymentMethodUpdate);
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {company.billing_company_id ? (
              <Button
                className="shrink-0"
                onClick={() => openStripeBillingPortal()}
                variant={ButtonVariant.Secondary}
                size={Size.SMALL}
                isLoading={portalLoadingState === LoadingState.LOADING}
              >
                Manage plan &amp; invoices
              </Button>
            ) : null}
          </div>
          <dl className="grid border-t border-border-light sm:grid-cols-3">
            <div className="px-4 py-3">
              <dt className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
                <span>Licenses</span>
                {licenseUtilization ? (
                  <RouterLink
                    to={appPaths.settingsTeammates.build()}
                    className={linkStyle({
                      className: "shrink-0 text-xs font-medium",
                    })}
                  >
                    Manage
                  </RouterLink>
                ) : null}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-foreground tabular-nums">
                {licenseUtilization ? (
                  <span className="flex flex-col gap-0.5">
                    {licenseUtilization.productUtilization.map((product) => (
                      <span key={product.product_key}>
                        {`${
                          teammateLicenseCopy[
                            getTeammateLicenseTypeForProductKey(
                              product.product_key,
                            )
                          ].label
                        }: ${formatUsageAgainstIncluded(
                          product.used_licenses,
                          product.max_licenses,
                        )}`}
                      </span>
                    ))}
                  </span>
                ) : (
                  "Not available"
                )}
              </dd>
            </div>
            <div className="border-t border-border-light px-4 py-3 sm:border-t-0 sm:border-l">
              <dt className="text-xs font-medium text-muted-foreground">
                Audio minutes
              </dt>
              <dd className="mt-1 text-sm font-semibold text-foreground tabular-nums">
                {currentAudioUsage
                  ? formatUsageAgainstIncluded(
                      currentAudioUsage.audio_minutes_used,
                      currentAudioUsage.included_audio_minutes,
                    )
                  : "—"}
              </dd>
            </div>
            <div className="border-t border-border-light px-4 py-3 sm:border-t-0 sm:border-l">
              <dt className="text-xs font-medium text-muted-foreground">
                Monthly AI credits
              </dt>
              <dd className="mt-1 text-sm font-semibold text-foreground tabular-nums">
                {currentUsage ? formatMonthlyCredits(currentUsage) : "—"}
              </dd>
            </div>
          </dl>
          <div className="border-t border-border-light px-4 py-3 text-xs text-muted-foreground">
            {portalLoadingState === LoadingState.ERROR ? (
              <p className="mb-1 text-red-700">
                {"Sorry, an error occurred. Please try again or "}
                <Link href="mailto:support@alpharun.com">
                  reach out to our team
                </Link>
                {" for help."}
              </p>
            ) : null}
            <p>
              {`Need help with your plan? `}
              <Link href="mailto:support@alpharun.com">Email us</Link>
              {" or "}
              <Link href="https://cal.frontapp.com/alpharun/paul/7fc09a3a">
                book a meeting
              </Link>
              {"."}
            </p>
          </div>
        </div>
      </section>
      {entitlements && entitlements.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">
            Plan limits
          </h2>
          <EntityTable
            aria-label="Entitlements"
            columns={entitlementColumns}
            entities={entitlements}
            isLoading={isLoadingEntitlements}
            isError={isErrorEntitlements}
            emptyMessage="No entitlements found."
          />
        </section>
      ) : null}
    </div>
  );
};
